import { put, take, select, takeLatest } from "redux-saga/effects";
import { get, isArray, isEmpty } from "lodash";
import {
    SET_FILTERED_ITEMS,
    SET_FILTERS,
    SET_FILTER_TYPES,
    SET_ITEM_FILTER,
    SET_FILTERED_MENU,
} from "../../actions/filterAction";
import {
    getFilterTypes,
    getItemFilter,
    getShowFilters,
    getMenuCache,
    getSelectedMenu,
    getLanguage,
    getFilteredMenu,
    getGuiTranslations,
} from "../selectors";
import { ItemFilter } from "../../models/itemFilter";
import { SET_ACTIVE_MENU_PATH, SET_MENU_CACHE } from "../../actions/configurationActions";
import { SET_SESSION_DATA } from "../../actions/sessionActions";
import { ItemType } from "../../models/itemType";
import { Filter, FilterTexts, FilterTypeMap } from "../../models/filter";
import { Item, MenuContents, MenuMap } from "../../../models/item";
import Constants from "../../../constants";
import { FrontendSettings } from "../../models/frontendSettings";
import TranslationMap from "../../../models/translationMap";

export function* resetItemFilterSaga(): any {
    while (yield take(SET_SESSION_DATA)) {
        const language: string = yield select(getLanguage);
        const itemFilter = new ItemFilter(language);

        yield put({
            type: SET_ITEM_FILTER,
            itemFilter,
        });
    }
}

export function* setFilterTypes(frontendSettings: FrontendSettings, appCustomization: any): any {
    const filterTypes: FilterTypeMap = {};

    Object.keys(frontendSettings).forEach(key => {
        const match = key.match(/filter_tags(_(.+))?/);
        if (!match) return;

        const type = match[2] || Constants.genericFilterKey;
        filterTypes[type] = frontendSettings[key].split(",").map((s: string) => s.trim());
    });

    // Override with the categories from the account app customization if specified.
    const accountFilterTexts: FilterTexts = {};
    const accountFilters = get(appCustomization, "ProductFilters.ProductTypeFilters");
    if (accountFilters) {
        Object.entries(accountFilters).forEach(([key, val]) => {
            const properties = get(val, "Properties");
            if (isArray(properties)) {
                const propArray: { Property: string; Text: string }[] = properties;
                if (propArray.length > 0) {
                    filterTypes[key] = propArray.map(p => p.Property);
                    accountFilterTexts[key] = Object.fromEntries(propArray.map(p => [p.Property, p.Text]));
                }
            }
        });
    }

    yield put({
        type: SET_FILTER_TYPES,
        filterTypes,
        accountFilterTexts,
    });
}

export function* filterItemsSaga(): any {
    yield takeLatest([SET_MENU_CACHE, SET_ACTIVE_MENU_PATH, SET_FILTERS, SET_ITEM_FILTER], function* (): any {
        const itemFilter: ItemFilter = yield select(getItemFilter);
        const menus: MenuMap = yield select(getMenuCache);
        const selectedMenu = yield select(getSelectedMenu);
        const menu = menus[selectedMenu];

        let filteredItems: Item[] = [];
        if (menu) {
            const isItemMenu = menu?.items?.some((item: Item) => item.type === ItemType.Item);
            filteredItems = isItemMenu ? itemFilter.filterItems(menu.items) : menu.items;
        }

        yield put({
            type: SET_FILTERED_ITEMS,
            filteredItems,
        });
    });
}

const filterItemsBySearch = (selecedAndFilteredMeny: any[], searchValueStr: string) => {
    const searchValueFilterFn = (element: any) => {
        return (
            (element.name && element.name.toLowerCase().indexOf(searchValueStr) > -1) ||
            (element.shortText && element.shortText.toLowerCase().indexOf(searchValueStr) > -1)
        );
    };

    return !isEmpty(selecedAndFilteredMeny)
        ? selecedAndFilteredMeny.filter((el: any) => {
              return searchValueFilterFn(el);
          })
        : selecedAndFilteredMeny;
};

export function* updateFiltersFromMenuItemsSaga(): any {
    yield takeLatest([SET_MENU_CACHE, SET_ACTIVE_MENU_PATH, SET_ITEM_FILTER, SET_FILTERED_MENU], function* (): any {
        const shouldShowFilters = yield select(getShowFilters);
        const itemFilter: ItemFilter = yield select(getItemFilter);
        const language: string = yield select(getLanguage);

        if (!shouldShowFilters || !itemFilter) return;

        const filterTypes: FilterTypeMap = yield select(getFilterTypes);
        const menus: MenuMap = yield select(getMenuCache);
        const selectedMenu: string = yield select(getSelectedMenu);

        const menuSearch: string = yield select(getFilteredMenu);
        const menu: MenuContents | undefined = menus[selectedMenu];

        const isItemMenu = menu && menu.items.some(item => item.type === ItemType.Item);
        const newItemFilter = itemFilter.clone();
        let filters: Filter[] = [];

        // filteredMenu is when user have search with textfield.
        if (menu && menu.items) {
            const filteredList =
                menuSearch.length > 0
                    ? filterItemsBySearch(menu.items, menuSearch.toLocaleLowerCase(language || undefined))
                    : menu.items;

            const guiTranslations: TranslationMap = yield select(getGuiTranslations);
            filters = newItemFilter.filterValues(menu.items, filteredList, filterTypes, guiTranslations);
        }

        if (!isItemMenu) {
            newItemFilter.clearCategory(Constants.genericFilterKey);
        }

        yield put({
            type: SET_FILTERS,
            filters,
            itemFilter: newItemFilter,
        });
    });
}
